// frontend/src/super-admin/announcements/AnnouncementsPage.tsx
//
// Send an announcement to tenants, and see what has been sent.
//
// IN-APP ONLY, deliberately: an announcement lands in each recipient's
// notification feed and nothing is emailed. The composer says so plainly, so
// nobody sends a "maintenance tonight" believing it hit an inbox.

import * as React from 'react';
import { toast } from 'sonner';
import { Megaphone, Send } from 'lucide-react';
import { api } from '../../core/api-client';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { PageHeader } from '../../design-system/components/PageHeader';
import { LoadingState } from '../../design-system/components/LoadingState';
import { ErrorState } from '../../design-system/components/ErrorState';
import { EmptyState } from '../../design-system/components/EmptyState';
import { VISUAL } from '../../design-system/visual';
import { useAdminPermission } from '../../core/auth/useAdminPermission';
import { useConfirm } from '../../components/ui/confirm-dialog';
import {
  announcementsApi,
  type Announcement,
  type AnnouncementAudience,
  type AnnouncementRecipients,
} from '../../modules/site-settings/api';

interface TenantOption { id: string; name: string; slug: string; status: string }

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AnnouncementsPage() {
  const confirm = useConfirm();
  const canSend = useAdminPermission('announcements.write');

  const [sent, setSent] = React.useState<Announcement[] | null>(null);
  const [tenants, setTenants] = React.useState<TenantOption[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);

  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [link, setLink] = React.useState('');
  const [audience, setAudience] = React.useState<AnnouncementAudience>('all');
  const [tenantId, setTenantId] = React.useState('');
  const [recipients, setRecipients] = React.useState<AnnouncementRecipients>('owners');

  const load = React.useCallback(() => {
    setError(null);
    setSent(null);
    announcementsApi.list()
      .then(setSent)
      .catch((e) => setError(String(e?.message ?? e)));
    api<{ tenants: TenantOption[] }>('/api/admin/tenants')
      .then((r) => setTenants(r.tenants.filter((t) => t.status !== 'cancelled')))
      .catch(() => setTenants([]));
  }, []);

  React.useEffect(() => { load(); }, [load]);

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    if (!title.trim()) { toast.error('A title is required'); return; }
    if (audience === 'tenant' && !tenantId) { toast.error('Pick a tenant'); return; }

    const who = audience === 'all'
      ? `every tenant (${tenants.length})`
      : (tenants.find((t) => t.id === tenantId)?.name ?? 'the selected tenant');
    const ok = await confirm({
      title: 'Send this announcement?',
      description:
        `It goes to ${recipients === 'owners' ? 'the owners of' : 'every active user in'} ${who}. ` +
        'It appears in their in-app notifications — no email is sent. ' +
        'There is no recall once it is delivered.',
      confirmLabel: 'Send',
    });
    if (!ok) return;

    setSending(true);
    try {
      const res = await announcementsApi.send({
        title: title.trim(),
        body: body.trim() || null,
        link: link.trim() || null,
        audience,
        tenant_id: audience === 'tenant' ? tenantId : null,
        recipients,
      });
      const a = res.announcement;
      // Report what actually happened, including nobody. "Sent" over zero
      // recipients is the kind of quiet lie this whole screen exists to avoid.
      if (a.delivered_count === 0) {
        toast.warning('Nobody received this', {
          description: 'No matching users were found in the selected tenants.',
        });
      } else if (res.failed_tenant_ids.length > 0) {
        toast.warning(
          `Delivered to ${a.delivered_count}, ${res.failed_tenant_ids.length} tenant(s) failed`,
          { description: 'The failures are recorded in the audit log. Re-sending is safe.' },
        );
      } else {
        toast.success(`Delivered to ${a.delivered_count} ${a.delivered_count === 1 ? 'person' : 'people'}`);
      }
      setTitle(''); setBody(''); setLink('');
      load();
    } catch (err: any) {
      toast.error('Send failed', { description: err?.message ?? String(err) });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Platform"
        title="Announcements"
        chip={
          <span className="text-[12px] text-ink/50 dark:text-white/50">
            In-app only — nothing is emailed
          </span>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-8">
        {canSend && (
          <form onSubmit={send} className={[VISUAL.bento, 'p-6 sm:p-7 space-y-4'].join(' ')}>
            <div className="flex items-center gap-2 mb-1">
              <Megaphone className="h-4 w-4" />
              <h3 className={VISUAL.dashSection}>New announcement</h3>
            </div>

            <div className="grid gap-1.5">
              <Label>Title<span className="text-destructive ml-0.5">*</span></Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Scheduled maintenance on Sunday"
                maxLength={200}
                required
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Message</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Worqly will be briefly unavailable between 02:00 and 03:00 UTC."
                maxLength={5000}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="grid gap-1.5">
                <Label>Audience</Label>
                <Select value={audience} onValueChange={(v) => setAudience(v as AnnouncementAudience)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tenants</SelectItem>
                    <SelectItem value="tenant">One tenant</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {audience === 'tenant' && (
                <div className="grid gap-1.5">
                  <Label>Tenant</Label>
                  <Select value={tenantId} onValueChange={setTenantId}>
                    <SelectTrigger><SelectValue placeholder="Pick a tenant" /></SelectTrigger>
                    <SelectContent>
                      {tenants.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid gap-1.5">
                <Label>Recipients</Label>
                <Select value={recipients} onValueChange={(v) => setRecipients(v as AnnouncementRecipients)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owners">Workspace owners only</SelectItem>
                    <SelectItem value="all_users">Every active user</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label>Link (optional)</Label>
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="/dashboard/billing or https://worqly.io/status"
              />
              <p className="text-[12px] text-muted-foreground">
                Must be a full URL or a path starting with <code className="font-mono">/</code>.
              </p>
            </div>

            <div className="flex justify-end pt-1">
              <Button
                type="submit"
                disabled={sending}
                className="rounded-full h-11 px-6 text-[14px] font-semibold gap-2"
              >
                <Send className="h-4 w-4" />
                {sending ? 'Sending…' : 'Send announcement'}
              </Button>
            </div>
          </form>
        )}

        <section>
          <h2 className="text-[13px] font-semibold uppercase tracking-[0.1em] text-ink/55 dark:text-white/55 mb-3">
            Sent
          </h2>
          {error ? (
            <ErrorState title="Couldn't load announcements" description={error} onRetry={load} />
          ) : sent === null ? (
            <LoadingState variant="inline" />
          ) : sent.length === 0 ? (
            <EmptyState
              title="Nothing sent yet"
              description="Announcements you send to tenants will be listed here."
            />
          ) : (
            <div className="rounded-2xl bg-white/70 dark:bg-white/[0.04] ring-1 ring-ink/8 dark:ring-white/10 divide-y divide-ink/8 dark:divide-white/10">
              {sent.map((a) => (
                <div key={a.id} className="px-5 py-4" data-testid="announcement-row">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-medium text-ink dark:text-white">{a.title}</div>
                      {a.body && (
                        <p className="text-[13px] text-ink/60 dark:text-white/60 mt-0.5 line-clamp-2">
                          {a.body}
                        </p>
                      )}
                      <div className="text-[12px] text-ink/45 dark:text-white/45 mt-1">
                        {a.audience === 'all' ? 'All tenants' : 'One tenant'}
                        {' · '}
                        {a.recipients === 'owners' ? 'owners' : 'all users'}
                        {' · '}
                        {formatWhen(a.created_at)}
                        {a.sent_by_label ? ` · ${a.sent_by_label}` : ''}
                      </div>
                    </div>
                    <div className="text-[12.5px] text-ink/60 dark:text-white/60 shrink-0">
                      {a.delivered_count} recipient{a.delivered_count === 1 ? '' : 's'}
                      {' · '}
                      {a.tenants_reached} tenant{a.tenants_reached === 1 ? '' : 's'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
